import * as React from "react";
import { Box, CircularProgress } from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import Shell from "./Shell";
import ModuleRouter from "../router/ModuleRouter";
import { useNotificationStream } from "../lib/useNotificationStream";

const LoginPage = React.lazy(() => import("../pages/LoginPage"));
const RegisterPage = React.lazy(() => import("../pages/RegisterPage"));
const PendingApprovalPage = React.lazy(() => import("../pages/PendingApprovalPage"));
const ChangeInitialPassword = React.lazy(() => import('../pages/ChangeInitialPassword'));

function FullScreenLoader() {
  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
      <CircularProgress size={36} />
    </Box>
  );
}

export default function AuthGate() {
  const { user, accessToken, loading, isPendingApproval } = useAuth();
  const [view, setView] = React.useState<"login" | "register">("login");

  if (loading) return <FullScreenLoader />;

  const isAuthenticated = !!user && !!accessToken;

  // Not authenticated — show login or register
  if (!isAuthenticated) {
    return (
      <React.Suspense fallback={<FullScreenLoader />}>
        {view === "login" ? (
          <LoginPage onSwitchToRegister={() => setView("register")} />
        ) : (
          <RegisterPage onSwitchToLogin={() => setView("login")} />
        )}
      </React.Suspense>
    );
  }

  // Authenticated but not approved
  if (isPendingApproval) {
    return (
      <React.Suspense fallback={<FullScreenLoader />}>
        <PendingApprovalPage />
      </React.Suspense>
    );
  }

  if (user.mustChangePassword) return <React.Suspense fallback={<FullScreenLoader />}><ChangeInitialPassword /></React.Suspense>;

  // Authenticated and approved — show main app
  return <AuthenticatedShell />;
}

function AuthenticatedShell() {
  useNotificationStream();
  return (
    <Shell>
      {(active) => (
        <Box
          sx={{
            px: { xs: 1.5, md: 2.5 },
            py: { xs: 1.75, md: 2.5 },
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          <ModuleRouter active={active} />
        </Box>
      )}
    </Shell>
  );
}
